import Swiper from 'swiper';
import {
    Autoplay,
    Controller,
    FreeMode,
    Keyboard,
    Mousewheel,
    Navigation,
    Pagination,
    Scrollbar,
    Thumbs, Virtual,
    EffectFade
} from 'swiper/modules';
import useBreakpoints from '../functions/useBreakpoints';

let SwiperElements = [];

const {breakpoints: tailwindBreakpoints} = useBreakpoints();

// Gebruik alleen cleanup als je de swiper async laadt

export const initDefaultSlider = (className, cleanup = false) => {
    const element = document.querySelector(className);
    if (isInitialized(element)) {
        return null;
    }

    SwiperElements.push(element);
    element.classList.add('swiper-init-loading');

    const navigation = getNavigation(element, {
        nextSelector: '.button-swiper-product-next',
        prevSelector: '.button-swiper-product-prev',
    });

    return new Swiper(className, {
        modules: [Navigation],
        loop: true,
        spaceBetween: 10,
        navigation,
        ...preventNavigation(cleanup),
    });
};

export const makeSwiper = (element, overrideOptions = {}, slidesPerViewLg = 5, cleanup = false) => {
    if (!(element instanceof HTMLElement)) {
        element = document.querySelector(element);
    }
    if (isInitialized(element)) {
        return null;
    }

    SwiperElements.push(element);
    element.classList.add('swiper-init-loading');

    const navigation = getNavigation(element, {
        nextSelector: '.button-swiper-next',
        prevSelector: '.button-swiper-prev',
    });

    let defaultOptions = {
        slidesPerView: 2,
        spaceBetween: 12,
        modules: [Navigation, Mousewheel],
        direction: 'horizontal',
        mousewheel: {
            forceToAxis: true,
        },

        navigation,
        ...preventNavigation(cleanup),

        breakpoints: {
            [parseInt(tailwindBreakpoints.md)]: {
                spaceBetween: 20,
                slidesPerView: 3,
            },
            [parseInt(tailwindBreakpoints.lg)]: {
                spaceBetween: 20,
                slidesPerView: slidesPerViewLg,
            },
        },
    };

    // merge with object to override standaard options
    const options = {...defaultOptions, ...overrideOptions};

    return new Swiper(element, options);
};

export const awardsSwiper = (element, overrideOptions = {}, slidesPerViewLg = 3, cleanup = false) => {
    if (!(element instanceof HTMLElement)) {
        element = document.querySelector(element);
    }
    if (isInitialized(element)) return null;

    SwiperElements.push(element);
    element.classList.add('swiper-init-loading');

    const navigation = getNavigation(element, {
        nextSelector: '.button-swiper-next',
        prevSelector: '.button-swiper-prev',
    });

    let defaultOptions = {
        autoplay: {
            delay: 5000,
            waitForTransition: true,
        },
        slidesPerView: 1,
        spaceBetween: 12,
        speed: 400,
        modules: [Navigation, Mousewheel, Autoplay],
        direction: 'horizontal',
        mousewheel: {
            forceToAxis: true,
        },

        navigation,
        ...preventNavigation(cleanup),

        breakpoints: {
            [parseInt(tailwindBreakpoints.md)]: {
                slidesPerView: 2,
            },
            [parseInt(tailwindBreakpoints.lg)]: {
                slidesPerView: slidesPerViewLg,
            },
        },
    };

    // merge with object to override standaard options
    const options = {...defaultOptions, ...overrideOptions};

    return new Swiper(element, options);
};

export const bundleTechSwiper = (element, overrideOptions = {}, cleanup = false) => {
    if (!(element instanceof HTMLElement)) {
        element = document.querySelector(element);
    }
    if (isInitialized(element)) return null;

    SwiperElements.push(element);
    element.classList.add('swiper-init-loading');

    const navigation = getNavigation(element, {
        nextSelector: '.button-swiper-next',
        prevSelector: '.button-swiper-prev',
    });

    let defaultOptions = {
        slidesPerView: 1,
        spaceBetween: 12,
        speed: 400,
        modules: [Navigation, Mousewheel],
        direction: 'horizontal',
        mousewheel: {
            forceToAxis: true,
        },

        navigation,
        ...preventNavigation(cleanup),

        breakpoints: {
            [parseInt(tailwindBreakpoints.md)]: {
                slidesPerView: 2,
            },
            [parseInt(tailwindBreakpoints.lg)]: {
                slidesPerView: 3,
            },
        },
    };

    // merge with object to override standaard options
    const options = {...defaultOptions, ...overrideOptions};

    return new Swiper(element, options);
};

export const scrollbarSwiper = (element, overrideOptions = {}, cleanup = false) => {
    if (!(element instanceof HTMLElement)) {
        element = document.querySelector(element);
    }

    if (isInitialized(element)) {
        return null;
    }

    SwiperElements.push(element);
    element.classList.add('swiper-init-loading');

    const scrollElement = element.parentElement.querySelector('.swiper-scrollbar');

    const navigation = getNavigation(element, {
        nextSelector: '.button-swiper-product-next',
        prevSelector: '.button-swiper-product-prev',
    });


    let defaultOptions = {
        slidesPerView: 1.5,
        spaceBetween: 12,
        modules: [Scrollbar, Navigation, Mousewheel, FreeMode],
        direction: 'horizontal',
        freeMode: {
            enabled: true,
            sticky: true,
        },
        mousewheel: {
            forceToAxis: true,
        },
        scrollbar: {
            el: scrollElement,
            draggable: true,
        },
        navigation,
        ...preventNavigation(cleanup),

        breakpoints: {
            [parseInt(tailwindBreakpoints.md)]: {
                spaceBetween: 20,
                slidesPerView: 3,
            },
            [parseInt(tailwindBreakpoints.lg)]: {
                spaceBetween: 20,
                slidesPerView: 5,
                freeMode: false,
            },
        },
    };

    // merge with object to override standard options
    const options = deepMerge(defaultOptions, overrideOptions);

    return new Swiper(element, options);

};

// Masonry-pagebuilderblok als mobiele carousel met scrollbar (< lg). De masonry
// (absolute layout) en deze Swiper sluiten elkaar uit: initPbMasonry() in
// pagebuilder.js zet onder lg de Swiper op en vernietigt 'm weer op lg+. We
// beheren de state daar via de instance-referentie, dus deze helper gebruikt
// bewust NIET de gedeelde isInitialized/SwiperElements-tracking (anders zou
// her-initialiseren na een resize geblokkeerd worden).
export function pbMasonrySwiper(row) {
    if (!(row instanceof HTMLElement)) {
        return null;
    }

    const container = row.parentElement;
    if (!container) {
        return null;
    }

    // Swiper-structuur op de bestaande masonry-DOM leggen: container = .swiper,
    // de .row-pb = .swiper-wrapper, elke .drag = .swiper-slide.
    container.classList.add('swiper', 'pb-masonry-swiper');
    row.classList.add('swiper-wrapper');
    row.querySelectorAll(':scope > .drag').forEach(slide => slide.classList.add('swiper-slide'));

    // Scrollbar-element injecteren (krijgt via .swiper-scrollbar-drag de mint-kleur).
    let scrollbar = container.querySelector(':scope > .swiper-scrollbar');
    if (!scrollbar) {
        scrollbar = document.createElement('div');
        scrollbar.className = 'swiper-scrollbar';
        container.appendChild(scrollbar);
    }

    return new Swiper(container, {
        modules: [Scrollbar, FreeMode, Mousewheel],
        slidesPerView: 1.1,
        spaceBetween: 12,
        direction: 'horizontal',
        freeMode: {
            enabled: true,
            sticky: true,
        },
        mousewheel: {
            forceToAxis: true,
        },
        scrollbar: {
            el: scrollbar,
            draggable: true,
        },
        breakpoints: {
            [parseInt(tailwindBreakpoints.md)]: {
                slidesPerView: 2.2,
                spaceBetween: 20,
            },
        },
    });
}

// Tegenhanger van pbMasonrySwiper: vernietigt de Swiper, ruimt de inline-styles
// en swiper-classes op, en verwijdert het geïnjecteerde scrollbar-element zodat
// de masonry-layout (lg+) op een schone DOM kan herstarten.
export function destroyPbMasonrySwiper(swiper, row) {
    if (swiper && typeof swiper.destroy === 'function') {
        swiper.destroy(true, true);
    }

    if (!(row instanceof HTMLElement)) {
        return;
    }

    const container = row.parentElement;
    row.classList.remove('swiper-wrapper');
    row.querySelectorAll(':scope > .drag').forEach(slide => slide.classList.remove('swiper-slide'));

    if (container) {
        container.classList.remove('swiper', 'pb-masonry-swiper', 'swiper-initialized', 'swiper-horizontal', 'swiper-backface-hidden');
        const scrollbar = container.querySelector(':scope > .swiper-scrollbar');
        if (scrollbar) {
            scrollbar.remove();
        }
    }
}

export function mediaSlider({
                                mainSliderSelector,
                                thumbsSliderSelector,
                                modalSliderSelector,
                                modalThumbSliderSelector,
                                modalId = 'productSwiperModal',
                                cleanup = false,
                            }) {
    let initialSlide,
        mainSliderWrapper,
        thumbsSliderWrapper;

    if (typeof mainSliderSelector === 'string') {
        mainSliderWrapper = document.querySelector(mainSliderSelector + ' .swiper-wrapper');
    } else {
        mainSliderWrapper = mainSliderSelector.querySelector('.swiper-wrapper');
    }

    if (!mainSliderWrapper) {
        return null;
    }

    const mainSliderContainer = mainSliderWrapper.parentElement;

    if (isInitialized(mainSliderContainer)) {
        return null;
    }

    SwiperElements.push(mainSliderContainer);

    mainSliderContainer.classList.add('swiper-init-loading');
    if (typeof thumbsSliderSelector === 'string') {
        thumbsSliderWrapper = document.querySelector(thumbsSliderSelector);
    } else {
        thumbsSliderWrapper = thumbsSliderSelector.querySelector('ul');
    }

    if (mainSliderWrapper) {
        //-- initialSlide is dependent on if there is a video present in the swiper on PDP
        initialSlide = mainSliderWrapper.querySelectorAll('iframe').length ?? 0;
    }

    const productSliderOptions = {
        modules: [EffectFade, Navigation, Thumbs, Controller, Keyboard],
        effect: 'fade',
        spaceBetween: 10,
        loop: true,
    };

    let swiperModalThumbs = null;
    if (document.querySelector(modalThumbSliderSelector)) {
        swiperModalThumbs = new Swiper(modalThumbSliderSelector, {
            modules: [Navigation],
            speed: 800,
            loop: false,
            spaceBetween: 10,
            slidesPerView: 'auto',
            centerInsufficientSlides: true,
            watchSlidesProgress: true,
            navigation: {
                enabled: true,
                prevEl: modalThumbSliderSelector + ' .button-swiper-product-prev-thumbs',
                nextEl: modalThumbSliderSelector + ' .button-swiper-product-next-thumbs',
            },
            observer: true,
            observeParents: true,
            // Make the thumbs slider slide the complete view instead of 1 slide:
            on: {
                afterInit: function () {
                    if(this.slides.length) {
                        // Calculate how many slides are visible
                        const swiperWidth = this.el.clientWidth;
                        const slideWidth = this.params.slidesPerView === 'auto'
                            ? this.slides[0].clientWidth
                            : swiperWidth / this.params.slidesPerView;

                        // Set slidesPerGroup to match visible slides
                        this.params.slidesPerGroup = Math.floor(swiperWidth / (slideWidth + this.params.spaceBetween));
                    }
                }
            }
        });
    }

    let carouselModalNavigation = getNavigation(modalSliderSelector, {
        nextSelector: '.button-swiper-product-next',
        prevSelector: '.button-swiper-product-prev',
    });

    let carouselModalOptions = {
        ...productSliderOptions,
        keyboard: {
            enabled: true,
        },
        watchSlidesProgress: true,
        observer: true,
        observeSlideChildren: true,
        initialSlide: initialSlide,
        //-- hidden-class workaround
        on: {
            init: function (swiper) {
                if (cleanup) {
                    destroyNavigation(swiper);
                }
                const modal = document.getElementById(modalId);
                modal.classList.remove('opacity-0', '-z-5');
                modal.classList.add('hidden');
            },
            activeIndexChange: function (swiper) {
                let intIndex = swiper.realIndex + 1;
                let intSwiperTotal = $('#productDetailThumbs_modal ul li').length;

                intSwiperTotal = intSwiperTotal === 0 ? 1 : intSwiperTotal;
                $('#active-index').html(intIndex + '/' + intSwiperTotal);
            },
        },
    };

    if (swiperModalThumbs) {
        carouselModalOptions.thumbs = {
            thumbsContainerClass: 'productDetailThumbs_modal',
            swiper: swiperModalThumbs ?? null,
            multipleActiveThumbs: false,
        };
    }
    if (carouselModalNavigation) {
        carouselModalOptions.navigation = carouselModalNavigation;
    }


    //-- Swiper: Carousel modal

    let swiperCarouselModal = null;
    let container = typeof modalSliderSelector === 'string' ? document.querySelector(modalSliderSelector) : modalSliderSelector;
    if (container) {
        swiperCarouselModal = new Swiper(container, carouselModalOptions);
    }

    let mainSliderNavigation = getNavigation(mainSliderSelector, {
        nextSelector: '.button-swiper-product-next',
        prevSelector: '.button-swiper-product-prev',
    });

    let carouselOptions = {
        ...productSliderOptions,
        controller: {
        },
        initialSlide: initialSlide,
        ...preventNavigation(cleanup),
    };

    if (mainSliderNavigation) {
        carouselOptions.navigation = mainSliderNavigation;
    }

    if (swiperCarouselModal) {
        carouselOptions.controller = {
            control: swiperCarouselModal,
        }
    }

    const swiperCarousel = new Swiper(mainSliderSelector, carouselOptions);
    swiperCarouselModal.controller.control = swiperCarousel;

    mainSliderWrapper.addEventListener('click', (e) => {
        //-- Set active index on init
        const activeIndex = Array.from(e.currentTarget.children).findIndex(slide =>
            slide.classList.contains('swiper-slide-active'),
        );
        updateActiveIndex(swiperCarouselModal, activeIndex, true);
    });

    //-- Thumb navigation functionality - no slider to prevent bugs on mac
    toggleThumb(swiperCarousel, $(thumbsSliderWrapper), initialSlide);
    //-- Extra functionality to update active thumb when swiping slides
    activeThumbOnSlide(swiperCarousel, $(thumbsSliderWrapper));

    //-- Make sure the modal slider is displaying active slide correctly
    $('#'+modalId).on('modal:open', function(e) {
       swiperCarouselModal.update();
    });

    return null;
}

function toggleThumb(carousel, thumbWrapper, initialSlide, updateIndex = false) {
    $(thumbWrapper).find('li.thumb:nth-child(' + (initialSlide + 1) + ')').addClass('active');

    $(thumbWrapper).find('li.thumb').on('click', function () {
        carousel.slideToLoop($(this).index());
        $(thumbWrapper).find('li.thumb.active').removeClass('active');
        $(this).addClass('active');

        updateActiveIndex(carousel, carousel.realIndex, updateIndex);
    });
}

function activeThumbOnSlide(carousel, thumbWrapper, updateIndex = false) {
    carousel.on('slideChange', function () {
        $(thumbWrapper).find('li.thumb').removeClass('active');
        $(thumbWrapper).find(`li.thumb[data-thumb="slide_${carousel.realIndex + 1}"]`).addClass('active');

        updateActiveIndex(carousel, carousel.realIndex, updateIndex);
    });
}

function updateActiveIndex(carousel, index, updateIndex = false) {
    if (updateIndex === true) {
        let total = carousel.slides.length;
        $('span#active-index').text(`(${index + 1}/` + total + `)`);
    }
}

function getNavigation(element, {
    nextSelector = '.button-swiper-product-next',
    prevSelector = '.button-swiper-product-prev',
}) {
    if (!(element instanceof HTMLElement)) {
        element = document.querySelector(element);
    }

    if (!element) {
        return null;
    }

    const nextElement = element.parentElement.querySelector(nextSelector);
    const prevElement = element.parentElement.querySelector(prevSelector);

    if (!nextElement || !prevElement) {
        return null;
    }

    return {
        nextEl: nextElement,
        prevEl: prevElement,
    };
};


export const updateScrollBarWidth = () => {
    let swiperScrollBars = document.querySelectorAll('.swiper-scrollbar');
    swiperScrollBars.forEach(function (scrollbar) {
        let swiperContainer = scrollbar.closest('.swiper-initialized');
        if (swiperContainer) {
            let extraScrollBarWidthPx = Math.ceil((window.innerWidth - swiperContainer.offsetWidth) / 2);
            scrollbar.classList.remove('!w-full');
            scrollbar.style.width = 'calc(100% + ' + extraScrollBarWidthPx + 'px)';
        }
    });
};


export function blogSwiper(element, cleanup = false) {
    if (isInitialized(element)) {
        return null;
    }

    const navigation = getNavigation(element, {
        nextSelector: '.button-swiper-next',
        prevSelector: '.button-swiper-prev',
    });

    if (navigation?.nextEl && navigation?.prevEl) {

        SwiperElements.push(element);
        return new Swiper(element, {
            effect: 'fade',
            modules: [Navigation, Mousewheel],
            slidesPerView: 'auto',
            spaceBetween: 5,
            navigation,
            ...preventNavigation(cleanup),
        });
    }

    return null;
}

// Pagebuilder swiper
export function pagebuilderSwiper(container, cleanup = false) {
    if (container === undefined) {
        console.error('No container defined for pagebuilderSwiper');
        return;
    }
    let objPagebuilderSwipers = $(`${container} .pb-carousel.swiper`);

    objPagebuilderSwipers.each(function () {
        // Get the current swiper item
        let $objSwiper = $(this);
        const swiperElement = $objSwiper[0];

        // check if element has swiper-wrapper child and at least one swiper-slide child
        if (!swiperElement.querySelector('.swiper-wrapper') || !swiperElement.querySelector('.swiper-slide')) {
            return;
        }

        if (isInitialized(swiperElement)) {
            return null;
        }
        $objSwiper.addClass('swiper-init-loading');

        // INITIALIZE
        // Get the number of visible slides
        let intVisibleSlides = $objSwiper.attr('data-swiper-visible-slides');
        // Get the number of slides to scroll
        let intNumberOfSlidesToScroll = $objSwiper.attr('data-swiper-per-slides');
        // Buttons or not
        let blnButtons = $objSwiper.attr('data-swiper-buttons');
        blnButtons = blnButtons === 'true' ? true : false;
        // Pagination or not
        let blnPagination = $objSwiper.attr('data-swiper-pagination');
        blnPagination = blnPagination === 'false' ? false : true;
        // Scrollbar or not
        let BlnScrollbar = $objSwiper.attr('data-swiper-scrollbar');
        BlnScrollbar = BlnScrollbar === 'false' ? false : true;
        //loop or not
        let blnSwiperLoop = $objSwiper.attr('data-swiper-loop');
        blnSwiperLoop = blnSwiperLoop === 'false' ? false : true;
        // Autoplay
        let intAutoPlay = $objSwiper.attr('data-swiper-autoplay');
        // Standard modules
        let arrSwiperModules = [Autoplay, Mousewheel];
        // Standard opacity class
        let strClass = 'opacity-0';
        let intSpaceBetween = 0;

        // Fallback if not initialized
        if (intVisibleSlides === undefined) {
            intVisibleSlides = 1;
        }

        if (intAutoPlay === undefined) {
            intAutoPlay = 0;
        }

        if ($objSwiper.parents().hasClass('pb-small-gutter')) {
            intSpaceBetween = 4;
        } else {
            intSpaceBetween = 20;
        }

        // Make every individual swiper its own unique function (settings via data attributes)
        // Initialize the Swiper configuration object without autoplay
        let swiperConfig = {
            modules: arrSwiperModules,
            direction: 'horizontal',
            loop: blnSwiperLoop,
            slidesPerView: 1,
            spaceBetween: intSpaceBetween,
            breakpoints: {
                480: {
                    slidesPerView: intVisibleSlides,
                },
            },
            ...preventNavigation(cleanup),
        };

        // Get the elements for the swiper
        const swiperElements = {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
            paginationEl: '.swiper-pagination',
            scrollbarEl: '.swiper-scrollbar',
        };

        // loop over the elements and check if they exist
        Object.keys(swiperElements).forEach(key => {
            let element = swiperElement.querySelector(swiperElements[key]);
            if (!element) {
                element = swiperElement.closest(swiperElements[key]);
            }

            swiperElements[key] = element;
        });

        if (swiperElements?.nextEl && swiperElements?.prevEl) {
            arrSwiperModules.push(Navigation);
            swiperConfig.navigation = {
                nextEl: swiperElements.nextEl,
                prevEl: swiperElements.prevEl,
            };
        }

        if (swiperElements.scrollbarEl) {
            arrSwiperModules.push(Scrollbar);
            swiperConfig.scrollbar = {
                el: swiperElements.scrollbarEl,
                hide: false,
                draggable: true,
            };
        }

        if (swiperElements.paginationEl) {
            arrSwiperModules.push(Pagination);
            swiperConfig.pagination = {
                el: swiperElements.paginationEl,
                clickable: true,
            };
        }

        // Check if strAutoplay is set and has a valid integer
        if (intAutoPlay && !isNaN(intAutoPlay) && Number(intAutoPlay) > 0) {
            // Add the autoplay property to the Swiper configuration object
            swiperConfig.autoplay = {
                delay: Number(intAutoPlay),
                disableOnInteraction: false,  // Optional: Set to false if you want autoplay to continue after user interaction
            };
        }

        // Initialize Swiper with the constructed configuration
        new Swiper(swiperElement, swiperConfig);
    });
}

function isInitialized(element) {
    if (SwiperElements.length === 0) {
        return false;
    }

    if (SwiperElements.includes(element)) {
        return true;
    }

    return element.classList.contains('swiper-initialized') || element.classList.contains('swiper-init-loading');
}

const removeEventListeners = el => el.parentNode.replaceChild(el.cloneNode(true), el);

function preventNavigation(cleanUp = false) {
    return {
        preventClicksPropagation: true,
        preventClicks: true,
        on: {
            init(swiper) {
                if (cleanUp) {
                    destroyNavigation(swiper);
                }
            },
        },
    };
}

function destroyNavigation(swiper) {
    if (!!swiper.navigation) {
        swiper.navigation.destroy();
    }
}

function deepMerge(target, source) {
    Object.keys(source).forEach(key => {
        if (source[key] && typeof source[key] === 'object') {
            if (!target[key]) {
                target[key] = {};
            }
            deepMerge(target[key], source[key]);
        } else {
            target[key] = source[key];
        }
    });

    return target;
}
